// schemas/event/event.transformer.js
import dayjs from "dayjs";
import { toDayjs } from "../../utils/helper";

// ============================================================================
// API FORMAT TRANSFORMER (Form → Firebase)
// ============================================================================

/**
 * Transforms form data to Firebase API format
 * @param {Object} formValues - React Hook Form values
 * @param {Object} context - Additional context (groupId, clubId, matchList, etc.)
 * @param {boolean} isParentGroup - Whether this is a parent group
 * @returns {Object} { success, data, metadata } or { success, error, fieldErrors }
 */
export function toApiFormat(
  formValues,
  {
    additionalLocations,
    groupId,
    clubId,
    matchList = [],
    matchDate,
    mode = "create",
  },
  isParentGroup = false,
) {
  // Handle showOnHomePage date transformations
  let showOnHomePageEnd;
  let showOnHomePageStart;

  if (formValues.showOnHomePage && formValues.showOnHomePage.length === 2) {
    const date1 = formValues.showOnHomePage[0];
    const date2 = formValues.showOnHomePage[1];

    const showOnHomePageStartTime = date1 > date2 ? date2 : date1;
    const showOnHomePageEndTime = date1 > date2 ? date1 : date2;

    showOnHomePageStart = toDayjs(showOnHomePageStartTime).unix();
    showOnHomePageEnd = toDayjs(showOnHomePageEndTime).unix();
  }

  // Prepare data with all transformations
  // Filter out invalid locations (null, undefined, or incomplete data)
  const validLocations = additionalLocations?.filter(
    (location) =>
      location &&
      location.id &&
      location.name &&
      location.locationData &&
      location.locationData.address !== undefined &&
      location.locationData.latitude !== undefined &&
      location.locationData.longitude !== undefined,
  );

  // Check if user has partially filled locations (incomplete data)
  const hasIncompleteLocations = additionalLocations?.some(
    (location) =>
      location &&
      (!location.id ||
        !location.name ||
        !location.locationData ||
        location.locationData.address === undefined ||
        location.locationData.latitude === undefined ||
        location.locationData.longitude === undefined),
  );

  if (hasIncompleteLocations) {
    return {
      success: false,
      error: {
        name: "LocationError",
        message:
          "Please select a valid location from the dropdown. Incomplete location data detected.",
      },
      fieldErrors: {
        additionalLocations: [
          "Please select a valid location from the dropdown",
        ],
      },
    };
  }

  // Normalize eventEndDate - always convert to a number
  // If null, empty string, or undefined, it means "no end date" -> use 9999999999
  // This ensures the schema always receives a number, not null or empty string
  const normalizedEventEndDate =
    formValues.eventEndDate === "" ||
    formValues.eventEndDate === null ||
    formValues.eventEndDate === undefined
      ? 9999999999
      : typeof formValues.eventEndDate === "number"
        ? formValues.eventEndDate
        : Number(formValues.eventEndDate) || 9999999999;

  const localData = {
    ...formValues,
    eventEndDate: normalizedEventEndDate,
    additionalLocations:
      validLocations && validLocations.length > 0 ? validLocations : undefined,
    matchId: String(formValues.matchId || ""),
    showOnHomePageEnd,
    showOnHomePageStart,
    groupId,
    clubId,
    matchList,
  };

  // Validate schema
  // const result = EventLocalSchema.safeParse(localData);

  // if (!result.success) {
  //   return {
  //     success: false,
  //     error: result.error,
  //   };
  // }

  const data = localData;

  // Initialize content object
  const content = {
    notifications: [],
    type: data.type,
    matchId: "",
    matchName: "",
    title: data.title || "",
    isHome: "",
    showOnHomePageStart: "",
    showOnHomePageEnd: "",
    awayTeam: { logo: "", team_id: "", team_name: "" },
    groupId: groupId,
    matchDate: matchDate,
  };

  // Create Details object
  content.details = {
    author: data.author || "",
    description: data.details || "",
    location: data.location || "",
    // eventEndDate is already a number from schema validation (normalized above)
    eventEndDate: data.eventEndDate,
    // Start date is optional for announcement types. Store null rather than
    // Number(null) === 0, which would place the content on 1 Jan 1970.
    eventStartDate:
      data.eventStartDate === null ||
      data.eventStartDate === undefined ||
      data.eventStartDate === ""
        ? null
        : Number(data.eventStartDate),
    // Time is optional. The timestamp alone cannot tell "no time chosen" from
    // "starts at 00:00", so record explicitly whether a time was picked.
    hasStartTime: Boolean(
      data.eventStartDate !== null &&
        data.eventStartDate !== undefined &&
        data.eventStartDate !== "" &&
        formValues.eventStartTime,
    ),
    image: typeof formValues.imageFile === "string" ? formValues.imageFile : "",
  };

  // Store chatChannel as STRING (channel name only)
  if (data.chatChannelName) {
    content.chatChannel = data.chatChannelName;
  }

  // Content links
  content.content = {
    articleLink: data?.articleLink,
    podcastLink: data?.podcastLink,
    videoLink: data?.videoLink,
  };

  // Groups
  if (isParentGroup) {
    content.groups =
      data.groups && data.groups.length > 0 ? data.groups : ["global"];
  } else {
    content.groups = [];
  }

  // Ticket linking
  content.linkedToTicket = data.isLinkTicket || false;
  content.ticketId = data.ticketId || "";
  content.ticketCode = data.ticketCode || "";
  content.ticketName = data.ticketName || "";
  content.ticketMatchId = data.ticketMatchId || "";

  // Additional locations
  if (
    data.additionalLocations &&
    data.additionalLocations.filter((location) => location).length > 0
  ) {
    content.additionalLocations = data.additionalLocations
      .filter((location) => location)
      .map((locData) => {
        const loc = locData.locationData;
        return {
          longitude: loc.longitude,
          latitude: loc.latitude,
          address: (loc.address + "").toString() === "" ? "" : loc.address,
          placeId: loc.placeId,
          name: locData.name,
          locationId: locData.id,
        };
      });
  }

  // Show on home page dates
  if (data.showOnHomePageStart) {
    content.showOnHomePageStart = data.showOnHomePageStart;
    content.showOnHomePageEnd = data.showOnHomePageEnd;
  }

  // Notifications
  if (data.notificationInUtc) {
    content.notifications = data.notificationInUtc.filter(
      (notification) => notification && dayjs(notification).isValid(),
    );
  }

  // Match handling

  if (data.matchId && data.matchId !== "") {
    content.matchId = data.matchId;

    if (mode === "edit") {
      content.matchName = data.matchName;
      content.isHome = data.isHome;
      content.awayTeam = data.awayTeam;
    } else {
      if (!data.matchList) {
        return {
          success: false,
          error: { message: "Unable to fetch match list, contact admin" },
          fieldErrors: { matchList: ["Match list is required"] },
        };
      }

      const matchDetails = data.matchList.find(
        (match) => Number(match?.fixture_id) === Number(data?.matchId),
      );

      if (!matchDetails || !data.clubId) {
        return {
          success: false,
          error: {
            message: "Unable to fetch match/club details, contact admin",
          },
          fieldErrors: { matchId: ["Invalid match or club details"] },
        };
      }

      content.isHome =
        Number(matchDetails.homeTeam.team_id) === Number(data.clubId);
      content.awayTeam = content.isHome
        ? matchDetails.awayTeam
        : matchDetails.homeTeam;
      content.matchName = `${matchDetails.homeTeam.team_name} vs ${matchDetails.awayTeam.team_name} (${dayjs(matchDetails.event_date).format("Do MMM, YYYY")})`;
    }

    if (
      !content.matchName ||
      content.isHome === undefined ||
      !content.awayTeam
    ) {
      return {
        success: false,
        error: { message: "Unable to fetch match details, contact admin" },
        fieldErrors: { matchId: ["Match details incomplete"] },
      };
    }
  }

  content.showForFans = data.showForFans;

  content.rsvpMax =
    data.rsvpMax != null && data.rsvpMax !== "" ? Number(data.rsvpMax) : null;

  content.checkIn = {
    ...(data.checkIn || {}),
  };

  content.volunteerRequests = {};
  if (data.volunteerRequests) {
    for (const request of data.volunteerRequests) {
      if (!request.title || !request.slotCount) {
        return {
          success: false,
          error: {
            message:
              "Title and # of slots are required for every volunteer slot.",
          },
          fieldErrors: {
            volunteerRequests: [
              "Title and # of slots are required for every volunteer slot.",
            ],
          },
        };
      }

      const id = request.id;
      // eslint-disable-next-line no-unused-vars
      const { id: _, ...requestData } = request;

      // Normalize startTime and endTime: convert empty strings to undefined, ensure numbers
      if (
        requestData.startTime !== undefined &&
        requestData.startTime !== null
      ) {
        requestData.startTime =
          typeof requestData.startTime === "string" &&
          requestData.startTime.trim() === ""
            ? undefined
            : Number(requestData.startTime);
      } else {
        requestData.startTime = undefined;
      }

      if (requestData.endTime !== undefined && requestData.endTime !== null) {
        requestData.endTime =
          typeof requestData.endTime === "string" &&
          requestData.endTime.trim() === ""
            ? undefined
            : Number(requestData.endTime);
      } else {
        requestData.endTime = undefined;
      }

      content.volunteerRequests[id] = requestData;
    }
  }

  content.videoConferenceLink = data.videoConferenceLink || "";
  content.meetingCode = data.meetingCode || "";

  return {
    success: true,
    data: content,
    metadata: {
      imageFile: formValues.imageFile,
      chatChannelName: data.chatChannelName || "",
      channelArchiveDateStart: data.channelArchiveDateStart || "",
      channelArchiveDateEnd: data.channelArchiveDateEnd || "",
    },
  };
}

// ============================================================================
// LOCAL FORMAT TRANSFORMER (Firebase → Form)
// ============================================================================

/**
 * Transforms Firebase data to form data format
 * @param {Object} apiData - Data from Firebase
 * @returns {Object} { success, data } or { success, error }
 */
export function toLocalFormat(apiData, isParent = false) {
  // const result = EventApiSchema.safeParse(apiData);

  // if (!result.success) {
  //   return {
  //     success: false,
  //     error: result.error,
  //   };
  // }

  const validated = apiData;

  const localData = {
    // Basic fields
    type: validated.type || "",
    matchId: validated.matchId || "",
    matchName: validated.matchName || "",
    title: validated.title || "",

    // Details
    author: validated.details?.author || "",
    details: validated.details?.description || "",
    location: validated.details?.location || "",
    imageFile: validated.details?.image || null,
    eventStartDate:
      validated.details?.eventStartDate !== undefined &&
      validated.details?.eventStartDate !== null
        ? validated.details.eventStartDate
        : undefined,
    eventEndDate:
      validated.details?.eventEndDate &&
      validated.details.eventEndDate !== 9999999999
        ? validated.details.eventEndDate
        : null,
    // undefined on documents saved before the flag existed
    hasStartTime:
      typeof validated.details?.hasStartTime === "boolean"
        ? validated.details.hasStartTime
        : undefined,

    // Chat channel
    chatChannelName: validated.chatChannel || "",
    channelArchiveDateStart: "",
    channelArchiveDateEnd: "",
    editConfiguration: { hasChannel: !!validated.chatChannel },

    // Show on home page
    showOnHomePageStart: validated.showOnHomePageStart,
    showOnHomePageEnd: validated.showOnHomePageEnd,

    // Notifications
    notificationInUtc:
      validated.notifications?.filter((notification) =>
        dayjs(notification).isValid(),
      ) || [],

    // Ticket linking
    isLinkTicket: validated.linkedToTicket || false,
    ticketId: validated.ticketId || "",
    ticketCode: validated.ticketCode || "",
    ticketName: validated.ticketName || "",
    ticketMatchId: validated.ticketMatchId || "",

    // Match data
    matchDate: validated.matchDate || "",
    awayTeam: validated.awayTeam || { logo: "", team_id: "", team_name: "" },
    homeTeam: validated.homeTeam || { logo: "", team_id: "", team_name: "" },
    isHome: validated.isHome,

    // Content links
    articleLink: validated.content?.articleLink || "",
    podcastLink: validated.content?.podcastLink || "",
    videoLink: validated.content?.videoLink || "",

    // Groups
    groups: validated.groups?.length > 0 ? validated.groups : ["global"],

    // Additional locations
    additionalLocations: [
      validated.mapsData,
      ...(validated.additionalLocations || []),
    ]
      .filter((locData) => locData)
      .map((locData) => ({
        name: locData.name || "",
        id: locData.locationId || "",
        locationData: {
          placeId: locData.placeId || "",
          address: locData.address || "",
          latitude: locData.latitude || "",
          longitude: locData.longitude || "",
        },
        hidden: false,
      })),

    // Show for fans
    showForFans: validated.showForFans || false,

    // RSVP max
    rsvpMax: validated.rsvp?.max ?? null,

    // Check-in
    checkIn: {
      validateGeolocation: true,
      ...(validated.checkIn || {}),
    },

    // Volunteer requests
    volunteerRequests: Object.entries(validated.volunteerRequests || {}).map(
      ([key, value]) => ({
        ...value,
        id: key,
      }),
    ),

    // Video conference
    videoConferenceLink: validated.videoConferenceLink || "",
    meetingCode: validated.meetingCode || "",
  };

  if (isParent) {
    localData.audience = validated.audience;
  }

  // Handle empty additional locations
  if (localData.additionalLocations.length === 0) {
    localData.additionalLocations = [undefined];
  }

  return {
    success: true,
    data: localData,
  };
}
